import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import Navbar from "../components/Navbar/Navbar";
import { Base_URL } from "../Client/apiURL";

const emptyMessage = { type: "", text: "" };

const surfaceSx = {
  borderRadius: "8px",
  border: "1px solid #E8E3F1",
  boxShadow: "0 8px 22px rgba(35, 25, 66, 0.05)",
};

const listItemSx = {
  border: "1px solid #ECE7F4",
  borderRadius: "8px",
  p: 1.5,
  backgroundColor: "#fff",
};

const mutedTextSx = { color: "#6B6478", fontSize: 13 };

const primaryButtonSx = {
  backgroundColor: "#6B41CB",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#5633A8", boxShadow: "none" },
};

const getInitial = (name, fallback = "?") => String(name || fallback).trim().charAt(0).toUpperCase();

const notifyTeacherAccessChanged = () => {
  window.dispatchEvent(new Event("teacherAccessRequestsChanged"));
};

const ResultAccess = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(emptyMessage);

  const pendingRequests = useMemo(
    () => requests.filter((request) => request.status === "pending"),
    [requests]
  );

  const approvedTeachers = useMemo(
    () => requests.filter((request) => request.status === "approved"),
    [requests]
  );

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${Base_URL}/app/users/teacher-access/requests`, {
        withCredentials: true,
      });
      if (response.data.responseCode === 200) {
        setRequests(response.data.response || []);
      } else {
        setMessage({ type: "error", text: response.data.message || "Could not load result access." });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Could not load result access." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const respondToRequest = async (linkId, action) => {
    setMessage(emptyMessage);
    try {
      const response = await axios.post(
        `${Base_URL}/app/users/teacher-access/requests/${linkId}/${action}`,
        {},
        { withCredentials: true }
      );
      if (response.data.responseCode === 200) {
        setMessage({ type: "success", text: response.data.message });
        await fetchRequests();
        notifyTeacherAccessChanged();
      } else {
        setMessage({ type: "error", text: response.data.message || "Could not update request." });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Could not update request." });
    }
  };

  const revokeAccess = async (linkId) => {
    setMessage(emptyMessage);
    try {
      const response = await axios.post(
        `${Base_URL}/app/users/teacher-access/links/${linkId}/remove`,
        {},
        { withCredentials: true }
      );
      if (response.data.responseCode === 200) {
        setMessage({ type: "success", text: response.data.message });
        await fetchRequests();
        notifyTeacherAccessChanged();
      } else {
        setMessage({ type: "error", text: response.data.message || "Could not revoke access." });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Could not revoke access." });
    }
  };

  const renderLoadingState = (label = "Loading result access...") => (
    <Box sx={{ border: "1px dashed #D8CFF0", borderRadius: "8px", p: 3, textAlign: "center" }}>
      <CircularProgress size={26} sx={{ color: "#6B41CB", mb: 1 }} />
      <Typography sx={{ fontWeight: 900, color: "#241B3A" }}>{label}</Typography>
    </Box>
  );

  const renderTeacherRow = (request) => (
    <Box key={request.id} sx={listItemSx}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ xs: "stretch", md: "center" }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Avatar src={request.teacherImageUrl || ""} sx={{ width: 42, height: 42 }}>
            {getInitial(request.teacherName, request.teacherId)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, color: "#241B3A" }} noWrap>
              {request.teacherName || "Teacher"}
            </Typography>
            <Typography sx={mutedTextSx} noWrap>
              {request.teacherEmail}
            </Typography>
          </Box>
        </Stack>
        <Chip
          size="small"
          label={request.status === "pending" ? "Waiting for approval" : "Can view results"}
          icon={request.status === "pending" ? <HourglassTopIcon /> : <CheckCircleIcon />}
          color={request.status === "pending" ? "warning" : "success"}
          sx={{ alignSelf: { xs: "flex-start", md: "center" }, borderRadius: "8px", fontWeight: 800 }}
        />
        {request.status === "pending" ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={() => respondToRequest(request.id, "approve")}
              sx={primaryButtonSx}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              onClick={() => respondToRequest(request.id, "decline")}
            >
              Decline
            </Button>
          </Stack>
        ) : (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => revokeAccess(request.id)}
          >
            Revoke
          </Button>
        )}
      </Stack>
    </Box>
  );

  return (
    <>
      <Navbar />
      <Box sx={{ width: "92%", maxWidth: 980, mx: "auto", pt: { xs: 10, md: 13 }, pb: 5 }}>
        <Stack spacing={2}>
          <Card sx={{ ...surfaceSx, boxShadow: "none" }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 }, "&:last-child": { pb: { xs: 2, md: 2.5 } } }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ManageAccountsIcon sx={{ color: "#6B41CB" }} />
                    <Typography variant="h4" sx={{ fontWeight: 900, color: "#241B3A", fontSize: { xs: 26, md: 32 } }}>
                      Result Access
                    </Typography>
                  </Stack>
                  <Typography sx={{ color: "#6B6478", mt: 0.5 }}>
                    Approve teachers who can view your IELTS and PTE results.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={`${pendingRequests.length} pending`}
                    color={pendingRequests.length ? "warning" : "default"}
                    sx={{ borderRadius: "8px", fontWeight: 800 }}
                  />
                  <Chip
                    label={`${approvedTeachers.length} approved`}
                    color={approvedTeachers.length ? "success" : "default"}
                    sx={{ borderRadius: "8px", fontWeight: 800 }}
                  />
                  {loading && (
                    <Chip
                      icon={<CircularProgress size={14} />}
                      label="Refreshing"
                      variant="outlined"
                      sx={{ borderRadius: "8px", borderColor: "#D9CEF4" }}
                    />
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={surfaceSx}>
            <CardContent sx={{ p: 2.25 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: "#241B3A" }}>
                    Pending Requests
                  </Typography>
                  <Typography sx={mutedTextSx}>Teachers waiting for your permission.</Typography>
                </Box>
                {loading ? (
                  renderLoadingState()
                ) : pendingRequests.length === 0 ? (
                  <Box sx={{ border: "1px dashed #D8CFF0", borderRadius: "8px", p: 3, textAlign: "center" }}>
                    <HourglassTopIcon sx={{ color: "#B7791F", fontSize: 34, mb: 1 }} />
                    <Typography sx={{ fontWeight: 900, color: "#241B3A" }}>No pending requests</Typography>
                    <Typography sx={{ color: "#6B6478", mt: 0.5 }}>
                      New teacher requests will appear here.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>{pendingRequests.map(renderTeacherRow)}</Stack>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={surfaceSx}>
            <CardContent sx={{ p: 2.25 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: "#241B3A" }}>
                    Approved Teachers
                  </Typography>
                  <Typography sx={mutedTextSx}>These teachers can currently view your results.</Typography>
                </Box>
                <Divider />
                {loading ? (
                  renderLoadingState()
                ) : approvedTeachers.length === 0 ? (
                  <Box sx={{ border: "1px dashed #D8CFF0", borderRadius: "8px", p: 3, textAlign: "center" }}>
                    <CheckCircleIcon sx={{ color: "#247A3D", fontSize: 34, mb: 1 }} />
                    <Typography sx={{ fontWeight: 900, color: "#241B3A" }}>No approved teachers yet</Typography>
                    <Typography sx={{ color: "#6B6478", mt: 0.5 }}>
                      Approved teachers will appear here.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>{approvedTeachers.map(renderTeacherRow)}</Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Snackbar
        open={Boolean(message.text)}
        autoHideDuration={3500}
        onClose={() => setMessage(emptyMessage)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={message.type || "info"}
          variant="filled"
          onClose={() => setMessage(emptyMessage)}
          sx={{ width: "100%" }}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ResultAccess;
